import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';
import { UserRouteDisplayComponent } from 'app/shared/components/user-route-display/user-route-display.component';
import { OrderDetailAttributes } from '@albatrosdeveloper/ave-models-package';

/**
 * Route/User Cell Renderer Component
 *
 * Reusable cell renderer for displaying route and user information.
 * Wraps the UserRouteDisplayComponent to show route name, code, username, and responsible business partner.
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'subRoute',
 *   cellRenderer: RouteUserCellRendererComponent
 * }
 * ```
 */
@Component({
  selector: 'app-route-user-cell-renderer',
  standalone: true,
  imports: [CommonModule, UserRouteDisplayComponent],
  template: `
    <app-user-route-display
      [routeData]="{
        routeName: routeName,
        externalKeys: {
          code: routeCode
        }
      }"
      [userData]="{
        name: userName,
        username: userName
      }"
      [responsibleData]="{
        name: responsibleName,
        personNumber: responsiblePersonNumber,
        externalKeys: {
          code: responsibleCode
        }
      }"
      [displayMode]="'detailed'"
      [showRouteCode]="true"
      [showUser]="true"
      [showResponsible]="true">
    </app-user-route-display>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RouteUserCellRendererComponent implements CellRenderer<any, any> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<OrderDetailAttributes, any>;

  /**
   * Get the full row data (element)
   */
  get element(): any {
    return this.params?.data;
  }

  /**
   * Get route name
   * Priority: subRoute.name -> route.name
   */
  get routeName(): string {
    return this.element?.subRoute?.name ??
           this.element?.route?.name ?? '-';
  }

  /**
   * Get route code
   * Priority: subRoute.externalKeys.code -> route.externalKeys.code
   */
  get routeCode(): string {
    return this.element?.subRoute?.externalKeys?.code ??
           this.element?.route?.externalKeys?.code ?? '-';
  }

  /**
   * Get user name
   * Priority: user (string) -> user.user_login.username -> username
   */
  get userName(): string {
    const user = this.element?.user;
    // Check if user is a string first
    if (typeof user === 'string') {
      return user;
    }
    // Otherwise check nested properties
    return user?.user_login?.username ??
           this.element?.username ?? '-';
  }

  /**
   * Get responsible name
   * Priority: subRoute.responsibleName -> responsibleName -> responsibleBusinessPartner.name -> responsible.name -> businessPartner.name
   */
  get responsibleName(): string {
    return this.element?.subRoute?.responsibleName ??
           this.element?.responsibleName ??
           this.element?.responsibleBusinessPartner?.name ??
           this.element?.responsible?.name ??
           this.element?.businessPartner?.name ?? '-';
  }

  /**
   * Get responsible code
   * Priority: subRoute.responsibleCode -> responsibleCode -> responsibleBusinessPartner.externalKeys.code -> responsible.externalKeys.code -> businessPartner.externalKeys.code
   */
  get responsibleCode(): string {
    return this.element?.subRoute?.responsibleCode ??
           this.element?.responsibleCode ??
           this.element?.responsibleBusinessPartner?.externalKeys?.code ??
           this.element?.responsible?.externalKeys?.code ??
           this.element?.businessPartner?.externalKeys?.code ?? '-';
  }

  /**
   * Get responsible person number
   * Priority: responsibleBusinessPartner.personNumber -> responsible.personNumber -> businessPartner.personNumber
   */
  get responsiblePersonNumber(): string {
    return this.element?.responsibleBusinessPartner?.personNumber ??
           this.element?.responsible?.personNumber ??
           this.element?.businessPartner?.personNumber ?? '-';
  }
}
